import type { Metadata } from "@/lib/head";
import { Link } from "@/lib/nav";

import EmptyState from "@buildpy/ui/empty-state";
import { sectionCardClass } from "@buildpy/ui/section-card";
import StatCard from "@/components/stat-card";
import StatusBadge from "@buildpy/ui/status-badge";
import ListTable from "@buildpy/ui/list-table";
import Money from "@/components/money";
import {
  TableCell,
  TableRow,
} from "@buildpy/ui/table";
import { dateAfterDays, EMPTY_MARK, formatDate } from "@buildpy/core/lib/format";
import { cn } from "@/lib/utils";
import { CUSTOMER_STAGE_LABEL } from "@buildpy/core/modules/customer/types";
import StageBadge from "@/modules/customer/ui/stage-badge";
import SetupChecklistCard from "@/modules/dashboard/ui/setup-checklist-card";
import UsageCard from "@/modules/plan/ui/usage-card";
import { isMissing } from "@buildpy/core/modules/dashboard/today";
import { listHref } from "@buildpy/core/modules/list/search";
import { START_SOON_DAYS } from "@buildpy/core/modules/project/dashboard";
import { PROJECT_STATUS_LABEL } from "@buildpy/core/modules/project/types";
import { WAITING_DAYS } from "@buildpy/core/modules/quote/dashboard";
import type { DashboardView } from "@buildpy/core/views/g3a/dashboard";
import { BRAND_NAME } from "@buildpy/ui/brand-paths";
import PageHeader from "@/shell/page-header";

// 대시보드는 /app 레이아웃과 같은 조각이라 템플릿이 안 붙는다 — 전체 제목을 직접 쓴다.
export const metadata: Metadata = { title: { absolute: `대시보드 · ${BRAND_NAME}` } };

// 「오늘 할 일」 한 칸 — 누르면 그 조건이 걸린 목록으로 간다.
interface TodoLine {
  key: string;
  label: string;
  count: number;
  note: string;
  // null 이면 숫자만 보이는 카드 — 갈 목록 화면이 없다.
  href: string | null;
  // 0 보다 크면 빨간 숫자로 — 놓치면 안 되는 칸만.
  urgent: boolean;
}

// 대시보드 — 위는 「오늘 뭘 챙기나」(할 일·시공중 현장), 아래는 「견적이 어떤가」(숫자 카드·표).
const Page = ({ data }: { data: DashboardView }) => {
  const { setup, awaiting, projectStats, board, kpis, rows, recent_customers } = data;

  const todo: TodoLine[] = [
    {
      key: "inquiries",
      label: "응대 안 한 문의",
      count: board.new_inquiries,
      note: `고객 단계 「${CUSTOMER_STAGE_LABEL.NEW}」`,
      href: listHref("/app/customers", { stage: "NEW" }),
      urgent: true,
    },
    {
      key: "not-logged",
      label: "오늘 일지 없는 현장",
      count: board.not_logged,
      note: `${PROJECT_STATUS_LABEL.IN_PROGRESS} ${board.in_progress}곳 중 · 작업일만 셈`,
      href: "#sites",
      urgent: true,
    },
    {
      key: "overdue",
      label: "착공일 지남",
      count: projectStats.start_overdue,
      note: `${PROJECT_STATUS_LABEL.SCHEDULED}인데 착공 예정일이 지남`,
      href: listHref("/app/projects", { due: "overdue" }),
      urgent: true,
    },
    {
      key: "waiting",
      label: "답 기다리는 견적",
      count: rows.length,
      note: `발송 후 ${WAITING_DAYS}일 이상`,
      href: "#waiting",
      urgent: false,
    },
    {
      key: "change-orders",
      label: "승인 대기 변경공사",
      count: awaiting.count,
      note: awaiting.note,
      href: awaiting.href,
      urgent: false,
    },
  ];

  return (
    <>
      <PageHeader
        description={`${data.memberName}님, 오늘 챙길 것부터 봅니다.`}
        title="대시보드"
      />

      {setup && !setup.allDone && <SetupChecklistCard checklist={setup} />}

      {data.planUsage && <UsageCard month={Number(dateAfterDays(0).slice(5, 7))} usage={data.planUsage} />}

      <section aria-labelledby="dashboard-todo" className="flex flex-col gap-y-3">
        <h2 className="text-lg font-semibold" id="dashboard-todo">오늘 할 일</h2>
        <ul className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5" data-testid="dashboard-todo">
          {todo.map((line) => {
            const body = (
              <>
                <span className="text-sm text-muted-foreground">{line.label}</span>
                <span className={cn("text-2xl font-semibold tabular-nums", line.urgent && line.count > 0 && "text-destructive")}>
                  {line.count}건
                </span>
                <span className="text-xs text-muted-foreground">{line.note}</span>
              </>
            );
            const cardClass = cn(sectionCardClass({ padding: "none", interactive: line.href !== null }), "flex h-full flex-col gap-y-1 px-4 py-3");
            return (
              <li data-testid={`dashboard-todo-${line.key}`} key={line.key}>
                {line.href ? (
                  <Link className={cardClass} href={line.href}>{body}</Link>
                ) : (
                  <div className={cardClass}>{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <section aria-labelledby="dashboard-sites" className="flex scroll-mt-4 flex-col gap-y-3" id="sites">
        <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1">
          <h2 className="text-lg font-semibold" id="dashboard-sites">
            {PROJECT_STATUS_LABEL.IN_PROGRESS} {board.in_progress}
            <span className="font-normal text-muted-foreground"> · 오늘 일지 없음 {board.not_logged}</span>
          </h2>
          <Link
            className="text-sm text-muted-foreground underline-offset-2 hover:underline"
            data-testid="dashboard-projects"
            href="/app/projects">
            현장 전체 · {PROJECT_STATUS_LABEL.SCHEDULED} {projectStats.scheduled} · {START_SOON_DAYS}일 안 착공 {projectStats.starting_soon} · 오늘 착공 {projectStats.start_today}
          </Link>
        </div>

        {board.sites.length === 0 ? (
          <EmptyState>{PROJECT_STATUS_LABEL.IN_PROGRESS}인 현장이 없습니다.</EmptyState>
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border" data-testid="dashboard-sites">
            {board.sites.map((site) => (
              <li key={site.id}>
                <Link
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5 hover:bg-muted"
                  href={isMissing(site) ? `/app/projects/${site.id}/today` : `/app/projects/${site.id}`}>
                  <span className="min-w-0 flex-1 truncate font-medium">{site.title}</span>
                  {site.end_d_day && (
                    <span className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">
                      준공 {site.end_d_day}
                    </span>
                  )}
                  <StatusBadge
                    label={site.logged_today ? "오늘 일지 있음" : site.workday ? "오늘 일지 없음" : "쉬는 날"}
                    tone={site.logged_today ? "done" : site.workday ? "new" : "hold"}
                  />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="dashboard-kpis" className="flex flex-col gap-y-3">
        <h2 className="text-lg font-semibold" id="dashboard-kpis">견적</h2>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="dashboard-kpis">
          {kpis.map((kpi) => (
            <StatCard
              key={kpi.key}
              hint={kpi.hint}
              href={kpi.href ?? undefined}
              title={kpi.title}
              value={kpi.amount === null ? kpi.value : <Money value={kpi.amount} />}
            />
          ))}
        </div>
      </section>

      <section className="flex scroll-mt-4 flex-col gap-y-3" id="waiting">
        <div className="flex items-baseline gap-x-2">
          <h2 className="text-lg font-semibold">답 기다리는 견적</h2>
          <span className="text-sm text-muted-foreground">
            발송 후 {WAITING_DAYS}일 이상 · {rows.length}건
          </span>
        </div>

        {rows.length === 0 ? (
          <EmptyState>
            {WAITING_DAYS}일 넘게 답을 기다리는 견적이 없습니다.
          </EmptyState>
        ) : (
          <ListTable
            columns={[
              { label: "번호" },
              { label: "고객" },
              { label: "금액", align: "right" },
              { label: "발송 후", align: "right" },
              { label: "열람" },
            ]}>
            {rows.map((row) => (
              <TableRow key={row.id} className="relative">
                <TableCell className="whitespace-nowrap px-3 py-2 font-medium">
                  <Link
                    className="after:absolute after:inset-0"
                    href={`/app/quotes/${row.id}`}>
                    {row.quote_no}
                    <span className="text-muted-foreground">
                      {" "}
                      v{row.version}
                    </span>
                  </Link>
                </TableCell>
                <TableCell className="whitespace-nowrap px-3 py-2">
                  {row.customer_name || EMPTY_MARK}
                </TableCell>
                <TableCell className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                  <Money value={row.total} />
                </TableCell>
                <TableCell className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                  {row.days_waiting}일
                </TableCell>
                <TableCell className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                  {row.viewed_at ? `열람 ${formatDate(row.viewed_at)}` : "미열람"}
                </TableCell>
              </TableRow>
            ))}
          </ListTable>
        )}
      </section>

      <section className="flex flex-col gap-y-3">
        <div className="flex items-baseline justify-between gap-x-2">
          <h2 className="text-lg font-semibold">최근 고객</h2>
          <Link
            className="text-sm text-muted-foreground underline-offset-2 hover:underline"
            href="/app/customers">
            전체 보기
          </Link>
        </div>

        {recent_customers.length === 0 ? (
          <EmptyState>
            아직 등록된 고객이 없습니다.
          </EmptyState>
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {recent_customers.map((customer) => (
              <li key={customer.id} className="relative">
                <Link
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5 hover:bg-muted"
                  href={`/app/customers/${customer.id}`}>
                  <span className="whitespace-nowrap font-medium">{customer.name}</span>
                  <StageBadge stage={customer.stage} />
                  <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground">
                    {customer.site_address || EMPTY_MARK}
                  </span>
                  <span className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">
                    {formatDate(customer.created_at)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
};

export default Page;
